import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestVoid, toSlotDto, toSlotFields, type Warning } from '@/shared/api'
import {
  BookingResultSchema,
  type CancelResult,
  CancelResultSchema,
  type Excursion,
  type ExcursionDraft,
  ExcursionListSchema,
  type ExcursionPatch,
  ExcursionResultSchema,
  ExcursionSchema,
  type ExcursionState,
} from '../model/schema'

export const excursionsQueryKey = ['excursions'] as const

export interface ExcursionsQuery {
  from: Date
  to: Date
  guideIds?: string[]
  states?: ExcursionState[]
  /** Сетке записи не нужны; карточке — нужны вместе с именами и телефонами. */
  withBookings: boolean
}

export function excursionsQueryOptions(params: ExcursionsQuery) {
  const query = {
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    guide_ids: params.guideIds ?? [],
    states: params.states ?? [],
    with_bookings: params.withBookings,
  }
  return queryOptions({
    queryKey: [...excursionsQueryKey, query] as const,
    queryFn: (): Promise<Excursion[]> => request('/excursions', ExcursionListSchema, { query }),
  })
}

export function excursionQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...excursionsQueryKey, 'one', id] as const,
    queryFn: (): Promise<Excursion> => request(`/excursions/${id}`, ExcursionSchema),
  })
}

export interface ExcursionResult {
  excursion: Excursion
  warnings: Warning[]
}

export function useCreateExcursionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draft: ExcursionDraft): Promise<ExcursionResult> =>
      request('/excursions', ExcursionResultSchema, {
        method: 'POST',
        body: {
          title: draft.title,
          ...toSlotFields(draft.slot),
          ...(draft.meetingPoint ? { meeting_point: draft.meetingPoint } : {}),
          guide_ids: draft.guideIds,
          ...(draft.capacity === null ? {} : { capacity: draft.capacity }),
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

/**
 * Одна ручка на всю правку выхода. Перетаскивание блока меняет и время, и
 * гида — двумя запросами это дало бы промежуточное состояние в сетке у
 * остальных, ровно как у дел.
 */
export function useUpdateExcursionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ExcursionPatch }): Promise<ExcursionResult> =>
      request(`/excursions/${id}`, ExcursionResultSchema, {
        method: 'PATCH',
        body: {
          ...(patch.title === undefined ? {} : { title: patch.title }),
          ...(patch.slot === undefined ? {} : { slot: toSlotDto(patch.slot) }),
          ...(patch.meetingPoint === undefined ? {} : { meeting_point: patch.meetingPoint }),
          ...(patch.guideIds === undefined ? {} : { guides: patch.guideIds }),
          ...(patch.capacity === undefined ? {} : { capacity: patch.capacity }),
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

export function useCancelExcursionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }): Promise<CancelResult> =>
      request(`/excursions/${id}/cancel`, CancelResultSchema, {
        method: 'POST',
        body: { reason },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

export function useMarkHeldMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      noShowBookingIds,
    }: {
      id: string
      noShowBookingIds: string[]
    }): Promise<Excursion> =>
      request(`/excursions/${id}/held`, ExcursionSchema, {
        method: 'POST',
        body: { no_show_booking_ids: noShowBookingIds },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

/** Сменить ссылку: прежняя перестаёт работать — так гасят утёкшую. */
export function useRotatePublicTokenMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<Excursion> =>
      request(`/excursions/${id}/public-token`, ExcursionSchema, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

/**
 * Убрать выход насовсем — вместе с записями клиентов и ссылкой на него. Это
 * уборка ошибочно заведённого: отмена (с причиной, обзвоном и уведомлением
 * гидов) — отдельная операция и никуда не девается.
 */
export function useDeleteExcursionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<void> =>
      requestVoid(`/excursions/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

export interface ManualBookingInput {
  excursionId: string
  name: string
  phone: string
  seats: number
  note: string
}

export function usePlaceManualBookingMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ excursionId, ...body }: ManualBookingInput) =>
      request(`/excursions/${excursionId}/bookings`, BookingResultSchema, {
        method: 'POST',
        body,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}

export function useCancelBookingMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      excursionId,
      bookingId,
      reason,
    }: {
      excursionId: string
      bookingId: string
      reason: string
    }): Promise<Excursion> =>
      request(`/excursions/${excursionId}/bookings/${bookingId}`, ExcursionSchema, {
        method: 'DELETE',
        body: { reason },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: excursionsQueryKey }),
  })
}
